import { putItem } from '../../../../support/queries';
import { store } from '../../../../support/store';
import type { Derivation, DerivationFormat } from '../composition/derive/evaluate';
import type { AttributeContext } from '../config/attributes';
import { refKey } from '../config/attributes';
import type { Stakeholder } from '../config/content';
import type { HelpLink, StepKind } from '../config/steps';
import { emptyWorkshopState, normalizeWorkshopState } from './emptyState';
import { summarise, summariseExposure } from '../modules/installBase/lifecycle';
// `parseFile` pulls the install-base parse worker (`new Worker(new URL(..., import.meta.url))`),
// which jest's non-module transform can't parse. It is only reached from the async
// `uploadInstallBase` method below, so it's imported lazily there — keeping the worker out of this
// module's static graph so leaf consumers of the resolvers (e.g. `config/liveFigures.ts`) stay
// unit-testable without dragging the worker in.
import type { NavigatorApi } from './types';
import type {
  AccountInsights,
  InstallBaseUpload,
  Session,
  TrainingState,
  Workshop,
  WorkshopState,
} from './types';

/**
 * Portal implementation of the Frontier AI runner's data contract.
 *
 * The original demo persisted the SE's whole `WorkshopState` blob to a bespoke
 * `/workshops/:id/state/` endpoint. Inside the portal there is no such endpoint:
 * a "workshop" IS the SE's `CampaignEnrollment` for the Frontier AI campaign,
 * and the blob lives on that row's `metadata.workshop`. So `workshopId` here is
 * the enrollment id, and a state write is a full-record PUT of the enrollment
 * (the Go runtime rejects partial PUT/PATCH — the whole record goes every time).
 *
 * The runner mounts with `setRunnerData` before the store hydrates, so the
 * enrollment record is always in `ctx` by the time a read or write arrives; that
 * record is what a PUT is layered onto, and each successful save writes the
 * server row back onto `ctx.record` so the next debounced flush layers onto the
 * freshest blob. Cross-remount freshness is the shared query cache's job: every
 * save invalidates `CacheKey.CampaignEnrollment` (see `workshopStore`), so the
 * portal refetches and hands a current record back on the next mount — there is
 * no bespoke enrollment cache here anymore.
 */

const ENROLLMENT = 'db/campaign-enrollment';

/**
 * Separator between the enrollment id and the per-delivery key inside a composite workshop id
 * (`<enrollmentId>~~<account:opportunity|temp>`). `~` is URL-unreserved and never appears in an
 * enrollment id (numeric) or a Salesforce id (alphanumeric), so the split is unambiguous.
 */
const DELIVERY_KEY_SEP = '~~';

/**
 * Split the runner's composite workshop id back into the enrollment row it PUTs and the per-delivery
 * sub-key its state lives under. A bare id (no separator) is the legacy context-free address — its
 * state stays in the single `metadata.workshop` blob (`deliveryKey === null`).
 */
function splitWorkshopId(workshopId: string): { recordId: string; deliveryKey: string | null } {
  const i = workshopId.indexOf(DELIVERY_KEY_SEP);
  if (i === -1) return { recordId: workshopId, deliveryKey: null };
  return {
    recordId: workshopId.slice(0, i),
    deliveryKey: workshopId.slice(i + DELIVERY_KEY_SEP.length) || null,
  };
}

/** Read one delivery's WorkshopState blob from an enrollment record (legacy single blob when unkeyed). */
function readWorkshopBlob(
  record: Record<string, any> | null | undefined,
  deliveryKey: string | null,
): Partial<WorkshopState> | undefined {
  if (!record?.metadata) return undefined;
  return deliveryKey
    ? (record.metadata.workshops as Record<string, WorkshopState> | undefined)?.[deliveryKey]
    : (record.metadata.workshop as WorkshopState | undefined);
}

/** Layer one delivery's blob back into a metadata bag without disturbing the others. */
function writeWorkshopMetadata(
  metadata: Record<string, any> | undefined,
  deliveryKey: string | null,
  blob: WorkshopState,
): Record<string, any> {
  const base = metadata ?? {};
  if (deliveryKey) {
    return { ...base, workshops: { ...(base.workshops ?? {}), [deliveryKey]: blob } };
  }
  return { ...base, workshop: blob };
}

export interface RunnerDataContext {
  campaignId: number;
  /** The CampaignEnrollment id — the DB row the runner PUTs back. */
  enrollmentId: string;
  /**
   * The id the runner/store/URL address workshop state by. A single enrollment is shared by every
   * delivery an SE walks in a campaign, so this is a per-delivery COMPOSITE — `enrollmentId~~<key>`
   * where `<key>` is `(account:opportunity)` (or a stable temp when no opportunity) — that isolates
   * each delivery's WorkshopState into its own `metadata.workshops[<key>]` sub-blob. Absent → falls
   * back to the bare `enrollmentId` and the legacy single `metadata.workshop` blob (context-free view).
   */
  workshopId?: string;
  /** The full enrollment record, needed to PUT a whole row back. */
  record: Record<string, any>;
  /** Resolved customer for this delivery, if the runner could derive one. */
  customer?: Workshop['customer'];
  /**
   * The Salesforce Opportunity id for this delivery, resolved from the SE's
   * campaign delivery linkage. The enrollment row itself carries no opportunity,
   * so this is how the runner learns which opportunity it is working.
   */
  opportunityId?: string;
  /**
   * The Salesforce Opportunity *name* for this delivery, resolved from the linkage at read time
   * (never stored). Carried alongside `opportunityId` so the runner can display the name and ground
   * the AI report on it instead of the bare id; absent → falls back to `opportunityId`.
   */
  opportunityName?: string;
  /**
   * The CampaignDelivery id this runner is working, when the enrollment is linked to one. It is the
   * customer-facing delivery (distinct from `enrollmentId`, the per-SE enablement row), and is what
   * per-delivery report charts resolve their live rows against (`useReportFigures` →
   * `resolveCampaignDeliveryData`). Absent → delivery-backed charts render empty until one is linked.
   */
  deliveryId?: number | string;
  /**
   * The campaign's DB-backed customer personas (CampaignCustomerPersona rows), in `order`, mapped
   * to the runner's `Stakeholder` shape by the portal. When present they replace the static
   * `STAKEHOLDERS` roster everywhere via `resolveStakeholders()`; absent (or empty) falls back to
   * the product default so the standalone demo and un-seeded campaigns still render.
   */
  personas?: Stakeholder[];
  /**
   * The campaign's DB-backed report-section config (CampaignReportSection rows), in `order`, mapped
   * to this minimal shape by the portal. When present it drives which report sections are AI-written
   * and which stored prompt writes each (replacing the runner's hard-coded `REPORT_SECTION_QUESTIONS`
   * / `DEFAULT_HIDDEN_SECTIONS` / `DATA_SECTION` constants); absent (or empty) falls back to those
   * bundled constants so the standalone demo and un-templated campaigns still render.
   */
  reportSections?: RunnerReportSection[];
  /**
   * The campaign's DB-backed report *figures* (CampaignReportFigure rows) — the charts and images an
   * admin arranged on the shared report canvas — in `order`, mapped to this minimal shape by the
   * portal. When present the runner renders THIS arrangement for every SE (filled with each delivery's
   * own numbers via `useReportFigures`), replacing the workshop's own `buildCharts` + picture
   * placements; absent (or empty) falls back to `state.reportLayout` so the standalone demo and
   * campaigns without an authored canvas still render their figures.
   */
  reportFigures?: RunnerReportFigure[];
  /**
   * The campaign's DB-authored per-step content (from each frontier step's `metadata.runner`, plus
   * its shared `metadata.helpLinks`), keyed by `kind`. When present, `resolveStepDef` merges each
   * entry over the matching bundled `StepDef`, so the runner renders DB-authored copy while its
   * structure (slug/kind/phase/order) stays bundled; absent (or a kind with no entry) falls back to
   * the bundled `config/steps.ts` so the standalone demo and un-seeded campaigns still render.
   */
  stepContent?: RunnerStepContent[];
  /**
   * The campaign's DB-backed capability/play catalogue (CampaignCapability rows), in `order`, mapped
   * to this decoupled shape by the portal. This is the single source that de-dupes the runner's three
   * inlined copies (`GUIDE_PLAYS`, `DEEP_DIVE_PLAYS`, `PLAY_PROPOSALS`); when present the reassembly
   * seams (`resolveDeepDivePlays` / `resolvePlayProposals`) prefer it, absent (or empty) falls back to
   * the bundled catalogues so the standalone demo and un-seeded campaigns still render.
   */
  capabilities?: RunnerCapability[];
  /**
   * The campaign's DB-backed generic content blocks (CampaignContentBlock rows), fetched flat across
   * every category and carried here in `order`. The reassembly seams in `resolveContent.ts` filter by
   * `category` and rebuild the runner's nested bundled shapes (deep-dive scenarios/sections, the
   * `content.ts` corpora) from them, preferring DB when the relevant category is present and falling
   * back to the bundled `config/*` lists otherwise. Predefined question options/cues ride `payload`
   * (non-AI); the lone AI hook is the opt-in `ai_question_config` category.
   */
  contentBlocks?: RunnerContentBlock[];
  /**
   * The campaign's DB-backed file assets (CampaignAsset rows: decks, PDFs, images), in `order`, mapped
   * to this decoupled shape by the portal. `resolveDeckSources` rebuilds the runner's `DECK_SOURCES`
   * map from the `deck`-kind rows (preferring an uploaded S3 `objectKey`, else the bundled public path
   * kept in `metadata.file`); absent (or empty) falls back to the bundled `DECK_SOURCES`.
   */
  assets?: RunnerAsset[];
  /**
   * The campaign's DB-backed progression phases (CampaignPhase rows), in `order`, mapped to this
   * decoupled shape by the portal. `config/phases.ts` (`phaseLabel`/`phaseOrder`) prefers these over
   * the bundled `PHASE_LABEL`/`PHASE_ORDER`; absent (or empty) falls back to the bundled constants so
   * the standalone demo and un-seeded campaigns keep today's fixed pre/workshop/post lifecycle. `key`
   * is the value each step's `phase` references.
   */
  phases?: RunnerPhase[];
  /**
   * The campaign's DB-backed AI flow definitions (CampaignPrompt rows), in `order`, mapped to this
   * decoupled shape by the portal. `config/prompts.ts` (`renderPrompt`) prefers a flow's DB `template`
   * over the bundled fallback string baked into each flow, filling the same `{token}` placeholders from
   * live grounding; absent (or a flow with no row) falls back to the bundled template so the standalone
   * demo and un-seeded campaigns keep today's prompts. `flowKey` is the stable id each flow resolves by.
   */
  prompts?: RunnerPrompt[];
  /**
   * The campaign's DB-backed intended outcomes (CampaignOutcome rows), in `order`, mapped to this
   * minimal shape by the portal. Consumed ONLY by the referenceable-attribute context
   * (`resolveAttributeReferences` → `{{outcome.*}}` tokens); the runner's flow logic does not read them.
   * Absent/empty ⇒ `{{outcome.*}}` tokens resolve verbatim (safe degrade).
   */
  outcomes?: RunnerOutcome[];
  /**
   * The campaign's DB-backed question pool (CampaignQuestion rows), in `order`, for the referenceable-
   * attribute context (`{{question.*}}` tokens) only. Absent/empty ⇒ those tokens resolve verbatim.
   */
  questionRefs?: RunnerQuestionRef[];
  /**
   * The campaign's DB-backed workshop team roles (CampaignWorkshopTeamRole rows), in `order`, for the
   * referenceable-attribute context (`{{role.*}}` tokens) only. Absent/empty ⇒ those tokens verbatim.
   */
  roles?: RunnerRoleRef[];
}

/**
 * A campaign intended outcome, reduced to what the referenceable-attribute context needs. The portal
 * maps `CampaignOutcome` DB rows into this. These rows have no native `key`; the attribute layer mints a
 * stable slug via `refKey(title, id)` so the picker's inserted `{{outcome.<key>.title}}` and the
 * resolver's lookup agree.
 */
export interface RunnerOutcome {
  id?: number | string;
  title: string;
  order?: number;
}

/** A campaign question-pool entry, reduced to what the attribute context needs (`{{question.*}}`). */
export interface RunnerQuestionRef {
  id?: number | string;
  text: string;
  order?: number;
}

/** A campaign workshop team role, reduced to what the attribute context needs (`{{role.*}}`). */
export interface RunnerRoleRef {
  id?: number | string;
  name: string;
  order?: number;
}

/**
 * One campaign AI flow definition, reduced to what the runner renders. The portal maps `CampaignPrompt`
 * DB rows into this so the guided subtree stays decoupled from the portal's campaign types. `flowKey`
 * is the stable id the AI flows resolve their template by; `template` is the prompt body with `{token}`
 * placeholders the flow fills from live grounding. `outputSchema`/`model`/`groundingInputs` are carried
 * for the admin builder and future generic marshalling; the flows consume only `template` today (the
 * model stays the runner's `FALLBACK_MODEL` default, seeded `null`).
 */
export interface RunnerPrompt {
  flowKey: string;
  label?: string;
  template: string;
  outputSchema?: Record<string, any>;
  model?: string | null;
  groundingInputs?: string[];
  metadata?: Record<string, any>;
}

/**
 * One campaign progression phase, reduced to what the runner renders. The portal maps `CampaignPhase`
 * DB rows into this so the guided subtree stays decoupled from the portal's campaign types. `key` is
 * the stable id each step's `phase` references; `label` is the progress-UI heading; `order` is the
 * unlock/progress order (rows arrive pre-sorted).
 */
export interface RunnerPhase {
  key: string;
  label: string;
  order: number;
  metadata?: Record<string, any>;
}

/**
 * One capability/play in the campaign's catalogue, reduced to what the runner renders. The portal maps
 * `CampaignCapability` DB rows into this so the frontier subtree stays decoupled from the portal's
 * campaign types (mirrors `RunnerStepContent`). `key` is the stable id the runner's play lookups
 * (`playById`) and proposal matching resolve against.
 */
export interface RunnerCapability {
  key: string;
  name: string;
  description: string;
  /** Rich (HTML) detail body — the deep-dive play detail. */
  detail: string;
  /** Report-ready recommendation prose used when this capability is proposed. */
  proposal: string;
  resourceLinks: Array<{ label: string; url: string }>;
}

/**
 * One generic content block, reduced to what the runner needs to reassemble a bundled corpus. The
 * portal maps `CampaignContentBlock` DB rows into this (kept close to the row so the reassembly seams
 * can rebuild any category). `payload` stays opaque here; each `resolveContent.ts` builder narrows it
 * to the bundled shape for its category. `parentId` carries the self-nesting hierarchy.
 */
export interface RunnerContentBlock {
  id: number | string;
  parentId: number | string | null;
  category: string;
  key: string;
  label: string;
  order: number;
  payload: Record<string, any>;
}

/**
 * One campaign file asset, reduced to what the runner needs to reference it. The portal maps
 * `CampaignAsset` DB rows into this. `objectKey` is the S3 key (empty until the file is uploaded);
 * `metadata` keeps the bundled `file`/`images`/`kicker`/`deck` so a deck still renders offline before
 * its bytes are in S3.
 */
export interface RunnerAsset {
  /** The `CampaignAsset` row id, so a signed download can be minted for the uploaded file. */
  id: number | string;
  key: string;
  kind: string;
  title: string;
  objectKey: string;
  contentType: string;
  metadata: Record<string, any>;
}

/**
 * A generic step → asset file reference the portal maps from a step's `metadata.files`. Names a `slot`
 * (the role the file plays for the step, e.g. `deck`) and the `CampaignAsset` it points to by `key`.
 * Kept local so the vendored frontier subtree stays decoupled from `support/campaigns` (`StepFileRef`).
 */
export interface RunnerFileRef {
  slot: string;
  assetKey: string;
  kind?: string;
  /** Optional author-editable, end-user-facing heading for this file in the step (see `StepFileRef.label`). */
  label?: string;
}

/**
 * One frontier step's DB-authored content, reduced to the fields the runner renders. The portal maps
 * a campaign step's `metadata.runner` (plus its `metadata.helpLinks`) into this so the frontier
 * subtree stays decoupled from the portal's campaign types (mirrors `RunnerReportSection`). Every
 * field is optional: `resolveStepDef` only overrides the ones present, leaving the rest bundled.
 */
export interface RunnerStepContent {
  /** Which bundled step this authors, matching `StepDef.kind` (=== the portal's `runnerKind`). */
  kind: StepKind;
  tag?: string;
  title?: string;
  subtitle?: string;
  cta?: string;
  liveEligible?: boolean;
  /** Authored resource links for the step header; from the step's shared `metadata.helpLinks`. */
  helpLinks?: HelpLink[];
  quickPrompts?: string[];
  /** Generic step → asset file references (from the step's `metadata.files`). */
  fileRefs?: RunnerFileRef[];
  /** Generic step → question content-block references (from the step's `metadata.questionRefs`). */
  questionRefs?: Array<{ slot?: string; category: string; key: string }>;
  /** Propose only: authored heading/description for the "Your conversation guide" block. */
  guideTitle?: string;
  guideBody?: string;
  /**
   * Authored in-step section headers (the `Block`/`SessionPresentation` headings + leads a step renders
   * inside its card), keyed by the stable section id in `STEP_SECTION_HEADINGS`. Each field is optional;
   * a blank/absent value keeps the component's bundled default. From `metadata.runner.sectionHeadings`.
   */
  sectionHeadings?: Record<string, { title?: string; lead?: string }>;
}

/**
 * One campaign report section, reduced to what the runner needs. The portal maps
 * `CampaignReportSection` DB rows into this so the frontier subtree stays decoupled from the portal's
 * campaign types (mirrors how personas are mapped to `Stakeholder`).
 */
export interface RunnerReportSection {
  sectionId: string;
  title?: string;
  order?: number;
  source?: 'template' | 'generated';
  isDataSection?: boolean;
  hiddenByDefault?: boolean;
  /** The bound `ai_question` id; when set, the section is AI-written. */
  questionId?: number;
  /**
   * The section's authored template prose, as `TemplateBlock[]` JSON (the same shape as the runner's
   * bundled `REPORT_TEMPLATE` section blocks). When non-empty this replaces the bundled prose for the
   * section; empty/absent falls back to the runner's `config/reportTemplate.ts`. Kept as `unknown[]`
   * so the api layer stays decoupled from the report engine's types (the adapter narrows it).
   */
  blocks?: unknown[];
  /**
   * Admin-authored body shown in place of AI generation when `aiEnabled` is false. Plain prose (one
   * paragraph per line). Absent/empty falls back to the section's `blocks`/bundled prose. An SE can
   * override this per delivery via `state.reportLayout.preloaded[sectionId]`.
   */
  preloadedContent?: string;
  /**
   * Whether the section auto-generates with AI on report load. `undefined`/`true` preserves the prior
   * auto-generate behavior; `false` seeds the section from `preloadedContent` and swaps the load-time
   * generation for an explicit "Rewrite with AI" action. An SE can flip this per delivery via
   * `state.reportLayout.aiDisabled[sectionId]`.
   */
  aiEnabled?: boolean;
  /**
   * Admin-authored grid placement (`{x,y,w,h}` in the unified report-canvas model, x/w cols out of
   * 12). The portal builds it from the row's `layout_*` columns when all four are set; absent means
   * the section renders full-width, stacked in reading order. Kept as a plain shape so the api layer
   * stays decoupled from the report engine's `ReportGridLayout` (the adapter narrows it).
   */
  layout?: { x: number; y: number; w: number; h: number };
}

/**
 * One campaign report figure, reduced to what the runner needs to render it. The portal maps
 * `CampaignReportFigure` DB rows into this so the frontier subtree stays decoupled from the portal's
 * campaign types (mirrors `RunnerReportSection`). Per-delivery chart data is NOT carried here — the
 * `chartQuery` declares how to derive it, and `useReportFigures` resolves it against the live delivery
 * at render time. Image bytes likewise ride a separate signed-URL path, not this shape.
 */
export interface RunnerReportFigure {
  /**
   * The `CampaignReportFigure` DB primary key. Distinct from `figureId` (the stable minted render id):
   * an image figure's signed download URL is fetched by this pk (`db/campaign-report-figure/{id}/
   * download-url/`). Absent for a figure not yet persisted.
   */
  id?: number | string;
  figureId: string;
  sectionId: string;
  kind: 'chart' | 'image';
  /** Flat render order across all figures; feeds the engine's `chartOrder`. */
  order: number;
  /** Gap index inside the section; feeds `figureAnchors`. Null/absent = end of section. */
  anchor?: number | null;
  title?: string;
  caption?: string;
  explanation?: string;
  chartKind?: 'bar' | 'doughnut' | 'line';
  tone?: string;
  /**
   * The chart's data recipe: either a static `{ bars: [...] }` literal, or a `Campaign Delivery`
   * tilebuilder query (`{ dataSource: 'Campaign Delivery', campaignDeliveryDataset, groupBy, ... }`)
   * whose delivery id is filled per render. Kept opaque here; `useReportFigures` narrows it.
   */
  chartQuery?: Record<string, unknown>;
  /** Storage key for an image figure's bytes (resolved to a signed URL / data URL separately). */
  imageObjectKey?: string | null;
  imageWidth?: number | null;
  imageHeight?: number | null;
  /**
   * Admin-authored grid placement on the unified report canvas (`{x,y,w,h}`, x/w cols out of 12).
   * The section-grid render (`modules/report/grid`) places prose sections; a figure keeps riding its
   * section's paragraph flow (`anchor`), so this is carried for the admin canvas to author against
   * and is not yet consumed by the runtime renderers. Absent when not placed on the grid.
   */
  layout?: { x: number; y: number; w: number; h: number };
}

let ctx: RunnerDataContext | null = null;

/**
 * Point the runner's data layer at one enrollment. Call before hydrating.
 *
 * The record is trusted as-is: it comes from the portal's shared react-query cache, which every
 * save invalidates (`workshopStore` → `CacheKey.CampaignEnrollment`), so a remount hands back a
 * current row rather than a stale one. Within a mount, `patchWorkshopState` keeps `ctx.record`
 * fresh by writing each saved server row back onto it, so successive debounced flushes layer onto
 * the latest blob without a bespoke cache to reconcile.
 */
export function setRunnerData(next: RunnerDataContext | null): void {
  ctx = next;
}

export function getRunnerData(): RunnerDataContext | null {
  return ctx;
}

/**
 * The personas to render/report for the active workshop, from the campaign's DB-backed roster. The
 * database is the single source of truth: no bundled fallback, so an unconfigured campaign returns
 * `[]` (GuidedRunner shows a "not configured" panel rather than a stale product roster). The
 * runner's PrepareStep and the report/summary export generators all read THIS, so the SE's persona
 * checklist and the exported document stay on a single source and their positional checklist keys
 * line up.
 */
export function resolveStakeholders(): Stakeholder[] {
  return ctx?.personas ?? [];
}

/**
 * The active campaign's report-section config, or `[]` when the portal supplied none (standalone
 * demo, or a campaign without a report template). Callers treat `[]` as "fall back to the runner's
 * bundled report constants", mirroring `resolveStakeholders`.
 */
export function resolveReportSections(): RunnerReportSection[] {
  return ctx?.reportSections ?? [];
}

/**
 * The active campaign's report-figure config, or `[]` when the portal supplied none (standalone demo,
 * or a campaign without an authored canvas). Callers treat `[]` as "fall back to the workshop's own
 * `state.reportLayout`", mirroring `resolveReportSections`.
 */
export function resolveReportFigures(): RunnerReportFigure[] {
  return ctx?.reportFigures ?? [];
}

/**
 * The section ids the active campaign's admin flagged hidden-by-default, or `[]` when there is no
 * DB report-section config (standalone demo / un-templated campaign). Threaded into the workshop
 * state's first-seed (`emptyWorkshopState` / `normalizeWorkshopState`) so those sections start out
 * in `reportLayout.hidden` — excluded from both the editor and the export — while remaining sections
 * the SE can un-hide. Only the section-section config carries this flag; figures have no
 * hidden-by-default concept (they are hidden via `reportLayout.chartsHidden`).
 */
function defaultHiddenSectionIds(): string[] {
  return resolveReportSections()
    .filter((section) => section.hiddenByDefault)
    .map((section) => section.sectionId);
}

/**
 * The active campaign's capability/play catalogue, or `[]` when the portal supplied none (standalone
 * demo, or a campaign whose capabilities were not seeded). The `resolveContent.ts` reassembly seams
 * treat `[]` as "fall back to the bundled play catalogues", mirroring `resolveStakeholders`.
 */
export function resolveCapabilities(): RunnerCapability[] {
  return ctx?.capabilities ?? [];
}

/**
 * The config-side half of the referenceable-attribute context (`{{group.key}}` resolution): the
 * per-delivery customer basics and the campaign's authored reference objects (personas, capabilities,
 * outcomes, questions, roles), all read from the module-level runner `ctx`. Pure and synchronous, like
 * the other `resolve*` seams, so both substitution seams (report/prose and AI prompts) can compose it
 * without a hook.
 *
 * The captured per-delivery data (discovery answers, open gaps, deep-dive findings) is NOT built here —
 * it lives with the callers that already hold the extraction helpers (`modules/attributeContext.ts`),
 * which merges it over this base. Reference rows with no native `key` (outcomes, questions, roles,
 * personas) get a stable slug via `refKey` so the picker's inserted key and the resolver's lookup agree.
 *
 * Every field degrades to absent (never blank): an un-seeded group simply yields no items, so its
 * `{{…}}` tokens resolve verbatim rather than leaking an empty string into customer-facing prose.
 */
export function resolveAttributeReferences(): AttributeContext {
  const personas = resolveStakeholders();
  const capabilities = resolveCapabilities();
  const outcomes = ctx?.outcomes ?? [];
  const questions = ctx?.questionRefs ?? [];
  const roles = ctx?.roles ?? [];
  return {
    customer: {
      name: ctx?.customer?.name ?? undefined,
      opportunity: ctx?.opportunityName ?? ctx?.opportunityId ?? undefined,
      accountId: ctx?.customer?.ccoAccountId ?? undefined,
    },
    references: {
      personas: personas.map((p, i) => ({ key: refKey(p.label, i), label: p.label })),
      capabilities: capabilities.map((c) => ({ key: c.key, name: c.name })),
      outcomes: outcomes.map((o, i) => ({
        key: refKey(o.title, typeof o.id === 'number' ? o.id : i),
        title: o.title,
        order: o.order,
      })),
      questions: questions.map((q, i) => ({
        key: refKey(q.text, typeof q.id === 'number' ? q.id : i),
        text: q.text,
      })),
      roles: roles.map((r, i) => ({
        key: refKey(r.name, typeof r.id === 'number' ? r.id : i),
        name: r.name,
      })),
    },
  };
}

/**
 * The active campaign's progression phases (in `order`), or `[]` when the portal supplied none
 * (standalone demo, or an un-seeded campaign). `config/phases.ts` treats `[]` as "fall back to the
 * bundled `PHASE_ORDER`/`PHASE_LABEL`", mirroring `resolveStakeholders`.
 */
export function resolvePhases(): RunnerPhase[] {
  return ctx?.phases ?? [];
}

/**
 * The active campaign's AI flow definitions (in `order`), or `[]` when the portal supplied none
 * (standalone demo, or an un-seeded campaign). `config/prompts.ts` treats `[]` (or a missing flow) as
 * "fall back to the bundled prompt template baked into the flow", mirroring `resolvePhases`.
 */
export function resolvePrompts(): RunnerPrompt[] {
  return ctx?.prompts ?? [];
}

/**
 * The active campaign's generic content blocks, optionally narrowed to one `category`, or `[]` when the
 * portal supplied none. The `resolveContent.ts` builders treat `[]` for a category as "fall back to the
 * bundled `config/*` list for that corpus", mirroring `resolveStakeholders`.
 */
export function resolveContentBlocks(category?: string): RunnerContentBlock[] {
  const all = ctx?.contentBlocks ?? [];
  if (!category) return all;
  // A block belongs to a category by its structural `category` OR by an additional membership listed in
  // `payload.categories` (a string[]). This is how one reference object (e.g. a question) can live in
  // several categories — Discovery + FAQ + Handling Pushback — without duplicating the row, since the
  // structural `category` column is single-valued (its unique constraint is campaign+category+key).
  return all.filter((b) => {
    if (b.category === category) return true;
    const extra = (b.payload as { categories?: unknown } | undefined)?.categories;
    return Array.isArray(extra) && extra.includes(category);
  });
}

/** Copy only the known {@link Derivation} fields off a block payload, so a malformed row can never
 * inject a non-`Derivation` shape into the evaluator. */
function derivationFromPayload(p: Record<string, any>): Derivation {
  const d: Derivation = {};
  if (Array.isArray(p.source)) d.source = p.source.filter((x: unknown): x is string => typeof x === 'string');
  if (typeof p.format === 'string') d.format = p.format as DerivationFormat;
  if (typeof p.template === 'string') d.template = p.template;
  if (typeof p.fallback === 'string') d.fallback = p.fallback;
  if (typeof p.skipEmpty === 'boolean') d.skipEmpty = p.skipEmpty;
  if (typeof p.trueLabel === 'string') d.trueLabel = p.trueLabel;
  if (typeof p.falseLabel === 'string') d.falseLabel = p.falseLabel;
  return d;
}

/**
 * Declarative {@link Derivation}s from the DB, keyed by block `key`. Two categories carry them:
 * `live_figure_derivation` (gap-row current-state figures, keyed by row `slug`) and
 * `report_token_derivation` (report `[Bracketed]` token fills, keyed by token name). Each block's
 * `payload` IS the serialized `Derivation`. `config/liveFigures.ts` and `modules/export/report.ts` read
 * these — the DB is the sole source, there is no bundled fallback. Lives here (the base data module)
 * rather than in `resolveContent` so `liveFigures` can consume it without an import cycle.
 */
export function resolveDerivations(category: string): Record<string, Derivation> {
  const out: Record<string, Derivation> = {};
  for (const b of resolveContentBlocks(category)) out[b.key] = derivationFromPayload(b.payload ?? {});
  return out;
}

/**
 * The active campaign's file assets, optionally narrowed to one `kind`, or `[]` when the portal supplied
 * none. `resolveDeckSources` treats `[]` as "fall back to the bundled `DECK_SOURCES`".
 */
export function resolveAssets(kind?: string): RunnerAsset[] {
  const all = ctx?.assets ?? [];
  return kind ? all.filter((a) => a.kind === kind) : all;
}

function sessionFromStore(): Session {
  const u = store.getState().user?.userData;
  return {
    user: {
      id: u?.id != null ? String(u.id) : 'me',
      name: u?.name ?? 'You',
      email: u?.email ?? undefined,
    },
  };
}

function workshopFromCtx(): Workshop {
  // The Workshop id must equal the id the URL/store address (the composite), so `useWorkshops()`
  // list lookups (`w.id === workshopId`) resolve to this delivery's row.
  const id = ctx?.workshopId ?? ctx?.enrollmentId ?? 'frontier';
  return {
    id,
    opportunityId: ctx?.opportunityId ?? (ctx?.record?.opportunity as string) ?? '',
    opportunityName: ctx?.opportunityName ?? undefined,
    customer: ctx?.customer ?? null,
    createdAt: (ctx?.record?.created_at as string) ?? undefined,
    updatedAt: (ctx?.record?.updated_at as string) ?? undefined,
  };
}

export const campaignData: NavigatorApi = {
  getSession: async () => sessionFromStore(),

  // The portal gates access to the campaign itself; there is no separate
  // training gate inside the runner, so training always reads complete.
  getTraining: async (): Promise<TrainingState> => ({
    userId: sessionFromStore().user.id,
    tool: true,
    frontierAi: true,
  }),
  completeTraining: async (): Promise<TrainingState> => ({
    userId: sessionFromStore().user.id,
    tool: true,
    frontierAi: true,
  }),

  listWorkshops: async () => (ctx ? [workshopFromCtx()] : []),
  createWorkshop: async () => {
    throw new Error('Workshops are created from the portal campaign, not the runner.');
  },

  getWorkshopState: async (workshopId) => {
    // `workshopId` is the runner's (possibly composite) address; read the enrollment row it maps to
    // and pull just this delivery's sub-blob so sibling deliveries never leak into it.
    const { deliveryKey } = splitWorkshopId(workshopId);
    const raw = readWorkshopBlob(ctx?.record ?? null, deliveryKey) ?? {};
    return normalizeWorkshopState(raw, workshopId, defaultHiddenSectionIds());
  },

  patchWorkshopState: async (workshopId, patch) => {
    const { recordId, deliveryKey } = splitWorkshopId(workshopId);
    const base =
      ctx?.record ??
      // Should not happen — the runner sets its context on mount — but never
      // drop a write: fall back to a bare record carrying just the blob.
      ({ id: recordId, metadata: {} } as Record<string, any>);

    const currentWorkshop =
      (readWorkshopBlob(base, deliveryKey) as WorkshopState | undefined) ??
      emptyWorkshopState(workshopId, defaultHiddenSectionIds());
    const nextWorkshop: WorkshopState = {
      ...currentWorkshop,
      ...patch,
      updatedAt: new Date().toISOString(),
    };
    const nextRecord = {
      ...base,
      id: base.id ?? recordId,
      // Merge only THIS delivery's sub-blob back in; the other deliveries' blobs on the row are left
      // exactly as they were.
      metadata: writeWorkshopMetadata(base.metadata, deliveryKey, nextWorkshop),
    };

    let saved: Record<string, any> = nextRecord;
    try {
      const res = await putItem(ENROLLMENT, nextRecord as any);
      if (res && typeof res === 'object') saved = res as Record<string, any>;
    } catch (err) {
      // Keep the optimistic record on ctx so the store's own retry (and any
      // flush before the retry lands) layers onto what we just tried to save.
      if (ctx && ctx.enrollmentId === recordId) ctx = { ...ctx, record: nextRecord };
      throw err;
    }
    // Write the server row back so the next debounced flush layers onto the freshest blob without a
    // stale-cache reconcile. Guarded on the enrollment id so a late save for a since-swapped delivery
    // never clobbers the context now pointing at a different one.
    if (ctx && ctx.enrollmentId === recordId) ctx = { ...ctx, record: saved };
    return (readWorkshopBlob(saved, deliveryKey) as WorkshopState) ?? nextWorkshop;
  },

  // The portal has no live Cisco Ready / IQ feed; the runner falls back to its
  // authored defaults and the SE's own capture, exactly as the demo does when a
  // backend cannot supply figures.
  getAccountInsights: async (): Promise<AccountInsights> => ({}),

  // Unused by the runner (ConnectCard parses in-browser and never uploads), but
  // the contract is honoured: parse client-side and return the categorised blob.
  uploadInstallBase: async (_workshopId, file): Promise<InstallBaseUpload> => {
    const { parseInstallBaseFile } = await import('../modules/installBase/parseFile');
    const parsed = await parseInstallBaseFile(file);
    const categories = summarise(parsed.rows);
    return {
      fileName: file.name,
      size: file.size,
      uploadedAt: new Date().toISOString(),
      rowCount: parsed.rows.length,
      categories,
      lifecycle: summariseExposure(categories),
    };
  },
};
